/**
 * Pure image-URL rewrite helpers (no DOM/window) shared by the runtime sweeper in
 * {@link ./cloudImageRewrite}. Kept here as plain functions so they can be unit
 * tested directly. See cloudImageRewrite.js for the full behavioural contract.
 */

/**
 * The one true origin for storefront product/category images.
 *
 * Built at runtime from parts via `Array.join` so the contiguous canonical-origin
 * literal NEVER appears in the compiled/minified bundle. This is essential:
 * serve.js rewrites every served JS file with
 * `content.split(GATSBY_CLIENT_TARGET).join(apiTarget)`, and on the preview /
 * alternate hosts `GATSBY_CLIENT_TARGET` IS the canonical image origin — so a plain
 * string literal here (or a `'https://' + '...'` concat, which the minifier folds
 * back to the literal) would itself be clobbered to the API host, turning this
 * rewrite into a no-op. The parts below never form the matched substring, and
 * `join` is not constant-folded by terser, so the value survives intact.
 */
export const CANONICAL_IMAGE_ORIGIN = ['https:', '', 'www.germanos.gr'].join('/');

// Colour swatches are self-hosted under static/images/colors — strip ANY host
// so they load from the local static dir.
const COLORS_RE = /https?:\/\/[^/"')\s]+\/(images\/colors\/[^"')?#]+\.png)/gi;

// Absolute image URL: capture protocol, host, and the `/images/...` path so the
// host can be swapped without disturbing the path.
const IMAGE_URL_RE = /(https?:)\/\/([^/"')\s]+)(\/images\/[^"')\s]*)/gi;

/** Colour-swatch pass: any host → local static `/images/colors/...` path. */
export function rewriteColors(value: string): string {
  if (!value || value.indexOf('/images/colors/') === -1) return value;
  return value.replace(COLORS_RE, '/$1');
}

/**
 * Host de-clobber pass: rewrite the host of any `/images/...` URL whose host is a
 * serve.js-stamped API-base host back to {@link CANONICAL_IMAGE_ORIGIN}.
 *
 * serve.js (commit "cors origin policy") substitutes the per-request API base
 * into served HTML/JS. On the preview / alternate-public hosts that base differs
 * from the hardcoded image base, and because the image base literal equals
 * `GATSBY_CLIENT_TARGET`, product-image hosts get rewritten too. This undoes
 * that — and ONLY that: genuine third-party/CDN image hosts (not in `hosts`),
 * relative URLs, non-`/images/` paths, and the canonical host are all untouched.
 *
 * @param value a URL, srcset, or style string (may contain many URLs)
 * @param hosts API-base hosts that must be treated as wrong
 * @returns value with stamped image hosts forced to the canonical CDN
 */
export function rewriteStampedImageHosts(value: string, hosts: Set<string>): string {
  if (!value || !hosts || hosts.size === 0 || value.indexOf('/images/') === -1) {
    return value;
  }
  return value.replace(IMAGE_URL_RE, (match, _proto, host, path) =>
    hosts.has(host) ? `${CANONICAL_IMAGE_ORIGIN}${path}` : match,
  );
}
